/**
 * PC外部ブラウザでLIFF URLにアクセスした場合、
 * パス情報がliff.stateクエリパラメータとして渡される。
 * 例: /sso?env=dev → ?liff.state=%2Fsso%3Fenv%3Ddev
 *
 * この関数はliff.stateパラメータを解析し、
 * 正しいパスとクエリパラメータでURLを書き換える。
 *
 * また、OAuthリダイレクトによるパスロスが発生した場合、
 * sessionStorageに保存されたパス情報を復元する。
 *
 * 注意: window.location.replace()を使用してページをリロードすることで、
 * React Routerが正しいパラメータで初期化されることを保証する。
 */

import { addLog, getPlatformInfo } from './debugLogger';

/** sessionStorageデータの有効期限（5分） */
const MAX_AGE_MS = 5 * 60 * 1000;

interface SavedPathData {
  path: string;
  timestamp: number;
}

/**
 * sessionStorageから保存パスを解析する。
 * JSON形式（新）と文字列形式（レガシー）の両方に対応。
 * 有効期限切れの場合はnullを返す。
 */
function parseSavedPath(raw: string): string | null {
  try {
    const parsed: SavedPathData = JSON.parse(raw);
    if (Date.now() - parsed.timestamp > MAX_AGE_MS) {
      console.warn('[LIFF] 保存パスが期限切れのためスキップします');
      return null;
    }
    return parsed.path;
  } catch {
    // JSON解析失敗 = レガシー形式。そのまま文字列として使用
    return raw;
  }
}

export function handleLiffState(): boolean {
  addLog('main', 'page_load', {
    url: window.location.href,
    referrer: document.referrer,
    ...getPlatformInfo(),
  });

  const url = new URL(window.location.href);
  const liffState = url.searchParams.get('liff.state');

  if (liffState) {
    // liff.stateが優先されるため、sessionStorageの保存パスをクリーンアップ
    try {
      sessionStorage.removeItem('flaro_liff_return_path');
    } catch {
      // sessionStorage利用不可は無視
    }

    // liff.stateをデコード（例: "/sso?env=dev"）
    let decodedState: string;
    try {
      decodedState = decodeURIComponent(liffState);
    } catch (e) {
      console.error(
        '[LIFF] liff.stateのデコードに失敗しました:',
        liffState,
        e
      );
      return false;
    }

    addLog('main', 'liff_state_detected', {
      liffState,
      decodedState,
    });

    // パスとクエリパラメータを分離
    const [path, queryString] = decodedState.split('?');

    // 新しいURLを構築
    const newUrl = new URL(window.location.origin);
    newUrl.pathname = path || '/';

    // liff.state以外の既存パラメータを保持
    // 注意: code, stateはLIFF認証に必要なOAuthパラメータなので保持する
    url.searchParams.forEach((value, key) => {
      if (key !== 'liff.state') {
        newUrl.searchParams.set(key, value);
      }
    });

    // liff.stateから抽出したクエリパラメータを追加
    if (queryString) {
      const stateParams = new URLSearchParams(queryString);
      stateParams.forEach((value, key) => {
        newUrl.searchParams.set(key, value);
      });
    }

    // ページをリロードして新しいURLでReactを初期化
    // これにより、React Routerが正しいパラメータを認識することを保証
    addLog('main', 'liff_state_redirect', {
      redirectTo: newUrl.toString(),
    });

    window.location.replace(newUrl.toString());
    return true; // リダイレクト中
  }

  // OAuthコールバック後のパス復元
  // liff.stateがなく、sessionStorageに保存パスがあり、現在/にいる場合
  let savedRaw: string | null = null;
  try {
    savedRaw = sessionStorage.getItem('flaro_liff_return_path');
  } catch {
    // sessionStorage利用不可 -- パス復元をスキップ
    return false;
  }

  if (savedRaw && url.pathname === '/') {
    try {
      sessionStorage.removeItem('flaro_liff_return_path');
    } catch (e) {
      console.warn('[LIFF] sessionStorageクリーンアップ失敗', e);
    }

    const savedPath = parseSavedPath(savedRaw);
    if (!savedPath) {
      return false;
    }

    // セキュリティ: savedPathが自アプリ内の相対パスであることを検証
    if (!savedPath.startsWith('/') || savedPath.startsWith('//')) {
      console.warn('[LIFF] 不正な保存パスを検出:', savedPath);
      return false;
    }

    const [path, queryString] = savedPath.split('?');
    const newUrl = new URL(window.location.href);
    newUrl.pathname = path;

    // 保存パスのクエリパラメータをマージ（既存パラメータ優先）
    if (queryString) {
      new URLSearchParams(queryString).forEach((value, key) => {
        if (!newUrl.searchParams.has(key)) {
          newUrl.searchParams.set(key, value);
        }
      });
    }

    addLog('main', 'restore_saved_path', {
      savedPath,
      redirectTo: newUrl.toString(),
    });

    window.location.replace(newUrl.toString());
    return true;
  }

  // 正しいパスに到着済みの場合はクリーンアップのみ
  if (savedRaw) {
    try {
      sessionStorage.removeItem('flaro_liff_return_path');
    } catch (e) {
      console.warn('[LIFF] sessionStorageクリーンアップ失敗', e);
    }
  }

  addLog('main', 'liff_state_none', {
    url: window.location.href,
  });

  return false; // リダイレクトなし
}
